class YCShippingProtection extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.toggleButton = this.querySelector('[data-yc-sp-toggle]');
    this.priceElement = this.querySelector('[data-yc-sp-price]');
    this.errorElement = this.querySelector('[data-yc-sp-error]');
    this.termsLink = this.querySelector('[data-yc-sp-terms]');
    this.onClick = this.handleClick.bind(this);
    this.onCartUpdate = this.handleCartUpdate.bind(this);
    this.onCheckoutClick = this.handleCheckoutClick.bind(this);
    this.onCheckoutSubmit = this.handleCheckoutSubmit.bind(this);
    this.stopTermsEvent = this.stopTermsEvent.bind(this);
    this.syncing = false;
    this.ensureTimer = null;
    this.checkoutPassthrough = false;
    this.acceleratedOverlay = null;
    this.lastCart = null;
    this.variantCatalog = null;
    this.variantCatalogPromise = null;
    this.variantCatalogComplete = false;
    this.variantCatalogLoadingTarget = 0;
    this.errorTimer = null;
    this.prewarmTimer = null;
    this.operationVersion = 0;

    this.addEventListener('click', this.onClick);
    this.termsLink?.addEventListener('click', this.stopTermsEvent);
    this.termsLink?.addEventListener('pointerdown', this.stopTermsEvent);
    document.addEventListener('cart:update', this.onCartUpdate);
    document.addEventListener('click', this.onCheckoutClick, true);
    document.addEventListener('submit', this.onCheckoutSubmit, true);

    if (this.dataset.autoRemoveOrphan === 'true') {
      window.setTimeout(() => this.removeOrphanProtection(), 0);
    }

    const manualPreference = this.getManualPreference();
    const overrideState = this.getStateOverride();
    if (manualPreference !== null) {
      this.syncCheckedState(manualPreference);
    } else if (overrideState !== null) {
      this.syncCheckedState(overrideState);
    } else if (this.dataset.defaultOn === 'true' && !this.isOptedOut()) {
      this.syncCheckedState(true);
    }

    if (this.isProtectionDesired()) {
      this.syncCheckoutGuards(this.fallbackCart());
    }

    this.scheduleVariantPrewarm();

    if (manualPreference !== false && this.dataset.defaultOn === 'true' && !this.isOptedOut()) {
      this.scheduleEnsureProtection(80);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.termsLink?.removeEventListener('click', this.stopTermsEvent);
    this.termsLink?.removeEventListener('pointerdown', this.stopTermsEvent);
    document.removeEventListener('cart:update', this.onCartUpdate);
    document.removeEventListener('click', this.onCheckoutClick, true);
    document.removeEventListener('submit', this.onCheckoutSubmit, true);
    window.clearTimeout(this.ensureTimer);
    window.clearTimeout(this.errorTimer);
    window.clearTimeout(this.prewarmTimer);
    this.acceleratedOverlay?.remove();
  }

  get variantId() {
    return Number(this.dataset.legacyVariantId || this.dataset.variantId || 0);
  }

  get productId() {
    return Number(this.dataset.productId || 0);
  }

  get productHandle() {
    return this.dataset.productHandle || '';
  }

  get protectionIdentity() {
    return this.productHandle || String(this.productId || this.variantId || 'default');
  }

  get checked() {
    return this.dataset.checked === 'true';
  }

  get ratePercent() {
    const rate = Number.parseFloat(this.dataset.ratePercent || '0');
    return Number.isFinite(rate) ? Math.max(0, rate) : 0;
  }

  get renderedFeeCents() {
    const cents = Number.parseInt(this.dataset.feeCents || '0', 10);
    return Number.isFinite(cents) ? Math.max(0, cents) : 0;
  }

  get optOutKey() {
    return `yc-shipping-protection-opt-out-${this.protectionIdentity}`;
  }

  get preferenceKey() {
    return `yc-shipping-protection-preference-${this.protectionIdentity}`;
  }

  get stateOverrideKey() {
    return `yc-shipping-protection-state-${this.protectionIdentity}`;
  }

  get cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  get sectionIds() {
    const ids = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);

    if (this.dataset.sectionId) ids.push(this.dataset.sectionId);

    return Array.from(new Set(ids));
  }

  handleClick(event) {
    if (event.target.closest('a')) return;
    if (!event.target.closest('[data-yc-sp-card]')) return;

    event.preventDefault();
    this.toggleProtection();
  }

  stopTermsEvent(event) {
    event.stopPropagation();
  }

  findCheckoutControl(target) {
    if (!(target instanceof Element)) return null;
    return target.closest('#checkout, button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
  }

  isCheckoutSubmit(form, submitter) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (submitter && this.findCheckoutControl(submitter)) return true;
    if (document.activeElement && document.activeElement.form === form) {
      return Boolean(this.findCheckoutControl(document.activeElement));
    }
    return false;
  }

  handleCheckoutClick(event) {
    if (this.checkoutPassthrough) return;

    const control = this.findCheckoutControl(event.target);
    if (!control) return;
    if (!this.needsCheckoutPreparation()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.prepareCheckout().then((ready) => {
      if (ready) this.continueCheckout(control);
    });
  }

  handleCheckoutSubmit(event) {
    if (this.checkoutPassthrough) return;

    const form = event.target;
    if (!this.isCheckoutSubmit(form, event.submitter)) return;
    if (!this.needsCheckoutPreparation()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.prepareCheckout().then((ready) => {
      if (!ready) return;

      this.checkoutPassthrough = true;
      if (event.submitter && typeof form.requestSubmit === 'function') {
        form.requestSubmit(event.submitter);
      } else {
        form.submit();
      }

      window.setTimeout(() => {
        this.checkoutPassthrough = false;
      }, 1500);
    });
  }

  continueCheckout(control) {
    this.checkoutPassthrough = true;

    const formId = control.getAttribute('form');
    const form = control.form || (formId ? document.getElementById(formId) : null) || control.closest('form');
    const href = control.getAttribute('href');

    if (form instanceof HTMLFormElement) {
      if (typeof form.requestSubmit === 'function' && control.matches('button, input')) {
        form.requestSubmit(control);
      } else {
        form.submit();
      }
    } else if (href) {
      window.location.href = href;
    } else {
      window.location.href = `${this.cartRoot}checkout`;
    }

    window.setTimeout(() => {
      this.checkoutPassthrough = false;
    }, 1500);
  }

  handleCartUpdate(event) {
    if (this.syncing) return;

    const cart = event.detail?.resource;
    if (cart?.items) {
      this.updateDisplayedPrice(cart);

      if (this.dataset.autoRemoveOrphan === 'true' && this.findProtectionItems(cart).length > 0 && !this.hasNonProtectionItems(cart)) {
        this.removeProtection(cart).catch(() => {});
        return;
      }

      const hasProtection = this.findProtectionItems(cart).length > 0;
      const hasProducts = this.hasNonProtectionItems(cart);
      const manualPreference = this.getManualPreference();
      const overrideState = this.getStateOverride();

      if (manualPreference === false) {
        this.syncCheckedState(false);
        window.clearTimeout(this.ensureTimer);
        if (hasProtection) this.removeProtection(cart).catch(() => this.scheduleRemoveProtection(500));
        return;
      }

      if (manualPreference === true) {
        this.syncCheckedState(true);
        if (hasProducts && !this.cartHasCurrentProtection(cart)) this.scheduleEnsureProtection(120);
        return;
      }

      if (overrideState !== null) {
        this.syncCheckedState(overrideState);
        if (overrideState && hasProducts && !this.cartHasCurrentProtection(cart)) this.scheduleEnsureProtection(120);
        if (!overrideState && hasProtection) this.removeProtection(cart).catch(() => {});
        return;
      }

      if (this.dataset.defaultOn === 'true' && !this.isOptedOut() && hasProducts) {
        this.syncCheckedState(true);
        if (!this.cartHasCurrentProtection(cart)) this.scheduleEnsureProtection(120);
        return;
      }

      this.syncCheckedState(hasProtection);
    }

    if (this.getManualPreference() !== false && this.dataset.defaultOn === 'true' && !this.isOptedOut()) {
      this.scheduleEnsureProtection(120);
    }
  }

  syncCheckedState(isChecked) {
    this.dataset.checked = isChecked ? 'true' : 'false';
    this.toggleButton?.setAttribute('aria-checked', isChecked ? 'true' : 'false');
  }

  setBusy(isBusy) {
    if (!this.toggleButton) return;
    this.toggleButton.disabled = isBusy;
    this.toggleButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  setError(message = '', autoClear = false) {
    window.clearTimeout(this.errorTimer);
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = message === '';

    if (message && autoClear) {
      this.errorTimer = window.setTimeout(() => this.setError(''), 4500);
    }
  }

  setCheckoutBusy(isBusy) {
    document.querySelectorAll('#checkout, button[name="checkout"], input[name="checkout"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;

      if (isBusy) {
        if (!button.dataset.ycSpWasDisabled) button.dataset.ycSpWasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        if (button.dataset.ycSpWasDisabled === 'false') button.disabled = false;
        button.removeAttribute('aria-busy');
        delete button.dataset.ycSpWasDisabled;
      }
    });
  }

  acceleratedCheckoutContainer() {
    return document.querySelector('.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart');
  }

  syncCheckoutGuards(cart) {
    this.lastCart = cart;
    this.syncAcceleratedCheckoutOverlay(cart);
  }

  syncAcceleratedCheckoutOverlay(cart) {
    const container = this.acceleratedCheckoutContainer();
    if (!container || !this.productHandle) {
      this.acceleratedOverlay?.remove();
      this.acceleratedOverlay = null;
      return;
    }

    const shouldGuard = this.syncing || !this.isCartSyncedForDesiredState(cart);

    if (!shouldGuard) {
      this.acceleratedOverlay?.remove();
      this.acceleratedOverlay = null;
      return;
    }

    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }

    if (!this.acceleratedOverlay || !this.acceleratedOverlay.isConnected) {
      this.acceleratedOverlay = document.createElement('button');
      this.acceleratedOverlay.type = 'button';
      this.acceleratedOverlay.setAttribute('aria-label', 'Prepare shipping protection before checkout');
      this.acceleratedOverlay.dataset.ycSpAcceleratedGuard = 'true';
      Object.assign(this.acceleratedOverlay.style, {
        position: 'absolute',
        inset: '0',
        zIndex: '10',
        border: '0',
        padding: '0',
        margin: '0',
        background: 'transparent',
        cursor: 'pointer',
      });
      this.acceleratedOverlay.addEventListener('click', (event) => this.handleAcceleratedGuardClick(event));
      container.appendChild(this.acceleratedOverlay);
    }
  }

  handleAcceleratedGuardClick(event) {
    if (this.checkoutPassthrough) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.prepareCheckout().then((ready) => {
      if (ready) this.syncAcceleratedCheckoutOverlay(this.lastCart || this.fallbackCart());
    });
  }

  setOptedOut(isOptedOut) {
    try {
      if (isOptedOut) {
        window.sessionStorage?.setItem(this.optOutKey, '1');
        window.localStorage?.setItem(this.optOutKey, '1');
      } else {
        window.sessionStorage?.removeItem(this.optOutKey);
        window.localStorage?.removeItem(this.optOutKey);
      }
    } catch (error) {}
  }

  setManualPreference(isChecked) {
    const value = isChecked ? 'on' : 'off';

    try {
      window.sessionStorage?.setItem(this.preferenceKey, value);
      window.localStorage?.removeItem(this.preferenceKey);
    } catch (error) {}
  }

  setStateOverride(isChecked, ttl = 8000) {
    try {
      window.sessionStorage?.setItem(
        this.stateOverrideKey,
        JSON.stringify({
          checked: Boolean(isChecked),
          expiresAt: Date.now() + ttl,
        })
      );
    } catch (error) {}
  }

  getStateOverride() {
    try {
      const raw = window.sessionStorage?.getItem(this.stateOverrideKey);
      if (!raw) return null;

      const state = JSON.parse(raw);
      if (!state || Number(state.expiresAt || 0) <= Date.now()) {
        window.sessionStorage?.removeItem(this.stateOverrideKey);
        return null;
      }

      return Boolean(state.checked);
    } catch (error) {
      return null;
    }
  }

  getManualPreference() {
    try {
      const value = window.sessionStorage?.getItem(this.preferenceKey);

      if (value === 'on') return true;
      if (value === 'off') return false;

      return null;
    } catch (error) {
      return null;
    }
  }

  isOptedOut() {
    try {
      return window.sessionStorage?.getItem(this.optOutKey) === '1' || window.localStorage?.getItem(this.optOutKey) === '1';
    } catch (error) {
      return false;
    }
  }

  isProtectionDesired() {
    const manualPreference = this.getManualPreference();
    if (manualPreference === false) return false;
    if (manualPreference === true) return true;
    return this.checked || (this.dataset.defaultOn === 'true' && !this.isOptedOut());
  }

  needsCheckoutPreparation() {
    if (this.syncing) return true;

    const cart = this.lastCart;
    if (this.isProtectionDesired()) {
      if (!cart?.items) return true;
      return !this.isCartSyncedForDesiredState(cart);
    }

    if (cart?.items && this.findProtectionItems(cart).length > 0) return true;

    return false;
  }

  isCurrentOperation(operationVersion, desiredChecked = null) {
    if (!operationVersion) return true;
    if (this.operationVersion !== operationVersion) return false;
    if (desiredChecked === null) return true;
    return this.checked === desiredChecked;
  }

  findProtectionItems(cart) {
    return (cart?.items || []).filter((item) => this.isProtectionItem(item));
  }

  isProtectionItem(item) {
    if (!item) return false;
    const properties = item.properties || {};
    return (
      properties._yc_shipping_protection === 'true' ||
      String(item.handle || '') === this.productHandle ||
      Number(item.product_id || 0) === this.productId ||
      (this.variantId > 0 && Number(item.variant_id || 0) === this.variantId)
    );
  }

  hasNonProtectionItems(cart) {
    return (cart?.items || []).some((item) => !this.isProtectionItem(item) && Number(item.quantity || 0) > 0);
  }

  protectionLineTotal(cart) {
    return this.findProtectionItems(cart).reduce((sum, item) => sum + Number(item.final_line_price || item.line_price || 0), 0);
  }

  protectionItemIsCurrent(item, selectedVariant = null) {
    if (Number(item?.quantity || 0) !== 1) return false;
    if (!selectedVariant) return true;
    return Number(item?.variant_id || 0) === Number(selectedVariant.id);
  }

  cartHasCurrentProtection(cart, selectedVariant = null) {
    const items = this.findProtectionItems(cart);
    return items.length === 1 && this.protectionItemIsCurrent(items[0], selectedVariant);
  }

  isCartSyncedForDesiredState(cart) {
    if (!cart?.items || !this.productHandle) return true;

    const hasProtection = this.findProtectionItems(cart).length > 0;
    const hasProducts = this.hasNonProtectionItems(cart);

    if (!this.isProtectionDesired() || !hasProducts || this.targetFeeCents(cart) <= 0) {
      return !hasProtection;
    }

    const selectedVariant = this.selectProtectionVariantFromCatalog(cart);
    return Boolean(hasProtection || (selectedVariant && this.cartHasCurrentProtection(cart, selectedVariant)));
  }

  baseCartTotal(cart) {
    return Math.max(0, Number(cart.total_price || 0) - this.protectionLineTotal(cart));
  }

  targetFeeCents(cart) {
    const baseTotal = this.baseCartTotal(cart);
    if (!baseTotal || !this.ratePercent) return 0;
    const rawFee = Math.floor((baseTotal * this.ratePercent) / 100);
    return this.roundDownFeeToLadder(rawFee);
  }

  roundDownFeeToLadder(rawFeeCents) {
    const fee = Math.max(0, Number(rawFeeCents || 0));
    if (!fee) return 0;
    if (fee <= 25) return 25;
    if (fee <= 250) {
      return 25 + (Math.floor((fee - 25) / 5) * 5);
    }
    if (fee <= 1200) {
      return Math.floor(fee / 10) * 10;
    }
    if (fee <= 3000) {
      return Math.floor(fee / 25) * 25;
    }
    if (fee < 6700) {
      return Math.floor(fee / 100) * 100;
    }
    return 6700;
  }

  storefrontAccessToken() {
    try {
      const featureNode = document.getElementById('shopify-features');
      const features = featureNode?.textContent ? JSON.parse(featureNode.textContent) : null;
      return features?.accessToken || '';
    } catch (error) {
      return '';
    }
  }

  storefrontCountryCode() {
    const activeCurrency = window.Shopify?.currency?.active;
    const currencyCountryMap = {
      AUD: 'AU',
      BRL: 'BR',
      CAD: 'CA',
      CHF: 'CH',
      DKK: 'DK',
      EUR: 'DE',
      GBP: 'GB',
      JPY: 'JP',
      NOK: 'NO',
      NZD: 'NZ',
      SEK: 'SE',
      USD: 'US',
    };
    const candidates = [
      window.Shopify?.country,
      document.documentElement?.dataset?.country,
      document.querySelector('meta[property="og:country-name"]')?.content,
      activeCurrency ? currencyCountryMap[activeCurrency] : '',
    ];

    return candidates.find((value) => /^[A-Z]{2}$/.test(String(value || ''))) || '';
  }

  storefrontVariantId(gid) {
    const match = String(gid || '').match(/ProductVariant\/(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  moneyToCents(amount) {
    const value = Number.parseFloat(String(amount || '0'));
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  async loadProtectionVariants(targetFeeCents = 0) {
    const targetFee = Math.max(0, Number(targetFeeCents || 0));

    if (this.variantCatalog?.length) {
      const highestKnownPrice = this.variantCatalog[this.variantCatalog.length - 1]?.priceCents || 0;
      if (this.variantCatalogComplete || !targetFee || highestKnownPrice >= targetFee) {
        return this.variantCatalog;
      }
    }

    if (this.variantCatalogPromise) {
      if (this.variantCatalogLoadingTarget >= targetFee || this.variantCatalogComplete) {
        return this.variantCatalogPromise;
      }

      await this.variantCatalogPromise.catch(() => {});
      if (this.variantCatalog?.length) {
        const highestKnownPrice = this.variantCatalog[this.variantCatalog.length - 1]?.priceCents || 0;
        if (this.variantCatalogComplete || !targetFee || highestKnownPrice >= targetFee) {
          return this.variantCatalog;
        }
      }
    }

    this.variantCatalogLoadingTarget = targetFee;
    this.variantCatalogPromise = this.fetchStorefrontProtectionVariants(targetFee)
      .then(({ variants, complete }) => {
        if (!variants.length) throw new Error('No shipping protection variants found');
        this.variantCatalog = variants.sort((a, b) => a.priceCents - b.priceCents);
        this.variantCatalogComplete = complete;
        return this.variantCatalog;
      })
      .finally(() => {
        this.variantCatalogPromise = null;
        this.variantCatalogLoadingTarget = 0;
      });

    return this.variantCatalogPromise;
  }

  scheduleVariantPrewarm(delay = 900) {
    window.clearTimeout(this.prewarmTimer);
    if (!this.productHandle || !this.ratePercent) return;

    this.prewarmTimer = window.setTimeout(() => {
      this.prewarmProtectionVariants();
    }, delay);
  }

  prewarmProtectionVariants() {
    const cart = this.lastCart || this.fallbackCart();
    const targetFee = this.targetFeeCents(cart) || this.renderedFeeCents || 250;
    this.loadProtectionVariants(targetFee).catch((error) => {
      console.warn('Shipping protection prewarm failed', error);
    });
  }

  async fetchStorefrontProtectionVariants(stopAtCents = 0) {
    const token = this.storefrontAccessToken();
    if (!token) throw new Error('Missing Storefront access token');

    const country = this.storefrontCountryCode();
    const contextDirective = country ? ' @inContext(country: $country)' : '';
    const countryVariable = country ? ', $country: CountryCode' : '';
    const query = `
      query YCShippingProtectionVariants($handle: String!, $first: Int!, $after: String${countryVariable})${contextDirective} {
        product(handle: $handle) {
          variants(first: $first, after: $after) {
            nodes {
              id
              title
              availableForSale
              price {
                amount
                currencyCode
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;
    const variants = [];
    let after = null;
    let complete = false;
    const stopAt = Math.max(0, Number(stopAtCents || 0));

    for (let page = 0; page < 12; page += 1) {
      const response = await fetch('/api/2026-04/graphql.json', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: {
            handle: this.productHandle,
            first: 250,
            after,
            ...(country ? { country } : {}),
          },
        }),
      });
      const data = await response.json();

      if (!response.ok || data.errors?.length) {
        throw new Error(data.errors?.[0]?.message || 'Unable to load shipping protection variants');
      }

      const connection = data.data?.product?.variants;
      if (!connection) throw new Error('Shipping protection product is unavailable');

      connection.nodes.forEach((variant) => {
        const id = this.storefrontVariantId(variant.id);
        const priceCents = this.moneyToCents(variant.price?.amount);
        if (id && priceCents > 0 && variant.availableForSale !== false) {
          variants.push({
            id,
            priceCents,
            currencyCode: variant.price?.currencyCode || 'USD',
            title: variant.title || '',
          });
        }
      });

      if (!connection.pageInfo?.hasNextPage) {
        complete = true;
        break;
      }

      if (stopAt > 0 && variants.some((variant) => variant.priceCents >= stopAt)) break;
      after = connection.pageInfo.endCursor;
    }

    return {
      variants,
      complete,
    };
  }

  selectProtectionVariantFromCatalog(cart) {
    const targetFee = this.targetFeeCents(cart);
    if (!targetFee || !this.variantCatalog?.length) return null;
    const highestKnownPrice = this.variantCatalog[this.variantCatalog.length - 1]?.priceCents || 0;
    if (targetFee > highestKnownPrice && !this.variantCatalogComplete) return null;

    let selectedVariant = null;
    for (let index = 0; index < this.variantCatalog.length; index += 1) {
      const variant = this.variantCatalog[index];
      if (variant.priceCents > targetFee) break;
      selectedVariant = variant;
    }

    if (selectedVariant) return selectedVariant;
    return this.variantCatalog[0];
  }

  async selectProtectionVariant(cart) {
    await this.loadProtectionVariants(this.targetFeeCents(cart));
    return this.selectProtectionVariantFromCatalog(cart);
  }

  fallbackCart() {
    const baseTotal = this.ratePercent > 0 && this.renderedFeeCents > 0 ? Math.floor((this.renderedFeeCents * 100) / this.ratePercent) : 0;

    return {
      items: baseTotal > 0 ? [{ variant_id: 0, quantity: 1, final_line_price: baseTotal }] : [],
      total_price: baseTotal,
      currency: 'USD',
    };
  }

  formatMoney(cents, currency = 'USD') {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  updateDisplayedPrice(cart) {
    if (!this.priceElement) return;
    const selectedVariant = this.selectProtectionVariantFromCatalog(cart);
    const priceCents = selectedVariant?.priceCents || this.targetFeeCents(cart);
    const currency = selectedVariant?.currencyCode || cart.currency || 'USD';
    this.priceElement.textContent = this.formatMoney(priceCents, currency);
    this.syncCheckoutGuards(cart);
  }

  async toggleProtection() {
    if (!this.productHandle || this.toggleButton?.disabled) return;

    this.setError('');
    const operationVersion = this.operationVersion + 1;
    this.operationVersion = operationVersion;
    const wasChecked = this.checked;
    const desiredChecked = !wasChecked;
    this.setManualPreference(desiredChecked);
    this.setStateOverride(desiredChecked);
    this.setOptedOut(!desiredChecked);
    if (!desiredChecked) window.clearTimeout(this.ensureTimer);
    this.setBusy(true);
    this.syncCheckedState(desiredChecked);

    try {
      const cart = await this.fetchCart();
      if (!this.isCurrentOperation(operationVersion, desiredChecked)) return;
      this.updateDisplayedPrice(cart);
      if (!desiredChecked) {
        const updatedCart = await this.removeProtection(cart);
        if (!this.isCurrentOperation(operationVersion, desiredChecked)) return;
        if (updatedCart) this.updateDisplayedPrice(updatedCart);
      } else {
        const updatedCart = await this.upsertProtection(cart, operationVersion);
        if (!this.isCurrentOperation(operationVersion, desiredChecked)) return;
        if (updatedCart) this.updateDisplayedPrice(updatedCart);
      }
      this.setStateOverride(desiredChecked, desiredChecked ? 2500 : 60000);
    } catch (error) {
      if (!this.isCurrentOperation(operationVersion, desiredChecked)) return;
      this.setStateOverride(desiredChecked, 60000);
      this.syncCheckedState(desiredChecked);
      if (desiredChecked) {
        this.scheduleEnsureProtection(600);
      } else {
        this.scheduleRemoveProtection(600);
      }
      console.error('Shipping protection update failed', error);
      this.setError('Protection is updating. Please try again.', true);
    } finally {
      if (this.operationVersion === operationVersion) this.setBusy(false);
    }
  }

  async removeOrphanProtection() {
    if (!this.productHandle) return;

    try {
      const cart = await this.fetchCart();
      if (this.findProtectionItems(cart).length > 0 && !this.hasNonProtectionItems(cart)) {
        const updatedCart = await this.removeProtection(cart);
        if (updatedCart) this.syncCheckoutGuards(updatedCart);
      }
    } catch (error) {}
  }

  async ensureDefaultProtection() {
    if (this.syncing || !this.productHandle || this.isOptedOut() || this.getManualPreference() === false) return;

    try {
      const cart = await this.fetchCart();
      this.updateDisplayedPrice(cart);

      if (!this.hasNonProtectionItems(cart)) {
        if (this.findProtectionItems(cart).length > 0) await this.removeProtection(cart);
        return;
      }

      const updatedCart = await this.upsertProtection(cart);
      if (updatedCart) this.updateDisplayedPrice(updatedCart);
      this.syncCheckedState(this.isProtectionDesired());
      this.setError('');
    } catch (error) {
      console.warn('Shipping protection background sync failed', error);
    }
  }

  scheduleEnsureProtection(delay = 120) {
    window.clearTimeout(this.ensureTimer);
    this.ensureTimer = window.setTimeout(() => this.ensureDefaultProtection(), delay);
  }

  scheduleRemoveProtection(delay = 120) {
    window.clearTimeout(this.ensureTimer);
    this.ensureTimer = window.setTimeout(async () => {
      try {
        const cart = await this.fetchCart();
        if (this.findProtectionItems(cart).length > 0) await this.removeProtection(cart);
      } catch (error) {}
    }, delay);
  }

  delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async fetchCart(attempts = 3) {
    let lastError = null;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        const response = await fetch(`${this.cartRoot}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error('Unable to fetch cart');
        return await response.json();
      } catch (error) {
        lastError = error;
        if (attempt < attempts) await this.delay(180 * attempt);
      }
    }

    throw lastError || new Error('Unable to fetch cart');
  }

  async upsertProtection(cart, operationVersion = 0) {
    const targetFee = this.targetFeeCents(cart);
    const protectionItems = this.findProtectionItems(cart);

    if (!targetFee) {
      return this.removeProtection(cart);
    }

    const selectedVariant = await this.selectProtectionVariant(cart);
    if (!selectedVariant) {
      throw new Error('No shipping protection variant is available for this cart total');
    }

    if (protectionItems.length === 1 && this.protectionItemIsCurrent(protectionItems[0], selectedVariant)) {
      this.syncCheckedState(true);
      return cart;
    }

    if (protectionItems.length > 0) {
      await this.removeProtection(cart);
      cart = await this.waitForProtectionRemoval();
    }

    return this.addProtectionLine(selectedVariant, targetFee, cart, operationVersion);
  }

  buildOptimisticProtectionCart(baseCart, selectedVariant, targetFee, addResponse = {}) {
    const baseItems = (baseCart?.items || []).filter((item) => !this.isProtectionItem(item));
    const protectionItem = {
      id: selectedVariant.id,
      variant_id: selectedVariant.id,
      product_id: this.productId,
      handle: this.productHandle,
      title: 'Worry-Free Purchase',
      product_title: 'Worry-Free Purchase',
      quantity: 1,
      final_line_price: selectedVariant.priceCents,
      line_price: selectedVariant.priceCents,
      properties: {
        _yc_shipping_protection: 'true',
        _yc_sp_rate_percent: String(this.ratePercent),
        _yc_sp_target_cents: String(targetFee),
        _yc_sp_price_cents: String(selectedVariant.priceCents),
      },
    };
    const baseTotal = this.baseCartTotal(baseCart || {});

    return {
      ...(baseCart || {}),
      item_count: baseItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0) + 1,
      items: [...baseItems, protectionItem],
      total_price: baseTotal + selectedVariant.priceCents,
      sections: addResponse.sections || baseCart?.sections || {},
    };
  }

  refreshProtectionCart(selectedVariant, optimisticCart, operationVersion = 0) {
    window.setTimeout(async () => {
      try {
        if (!this.isCurrentOperation(operationVersion, true)) return;
        const cart = await this.waitForProtectionCart(selectedVariant, 1600, 180);
        if (!this.isCurrentOperation(operationVersion, true)) return;
        if (cart?.items && this.cartHasCurrentProtection(cart, selectedVariant)) {
          this.updateDisplayedPrice(cart);
          this.dispatchCartUpdate({
            ...cart,
            sections: optimisticCart?.sections || {},
          });
          return;
        }

        if (this.isCurrentOperation(operationVersion, true) && this.isProtectionDesired()) this.scheduleEnsureProtection(500);
      } catch (error) {
        if (this.isCurrentOperation(operationVersion, true) && this.isProtectionDesired()) this.scheduleEnsureProtection(500);
      }
    }, 250);
  }

  async addProtectionLine(selectedVariant, targetFee, baseCart = null, operationVersion = 0) {
    this.syncing = true;
    try {
      const addResponse = await this.cartRequest('cart/add.js', {
        items: [
          {
            id: selectedVariant.id,
            quantity: 1,
            properties: {
              _yc_shipping_protection: 'true',
              _yc_sp_rate_percent: String(this.ratePercent),
              _yc_sp_target_cents: String(targetFee),
              _yc_sp_price_cents: String(selectedVariant.priceCents),
            },
          },
        ],
        sections: this.sectionIds.join(','),
        sections_url: window.location.pathname,
      });
      const cart = this.buildOptimisticProtectionCart(baseCart || this.lastCart || this.fallbackCart(), selectedVariant, targetFee, addResponse);

      this.syncCheckedState(true);
      this.syncCheckoutGuards(cart);
      this.dispatchCartUpdate(cart);
      this.refreshProtectionCart(selectedVariant, cart, operationVersion);

      return cart;
    } finally {
      this.syncing = false;
    }
  }

  async waitForProtectionCart(selectedVariant = null, timeout = 2200, interval = 140) {
    const startedAt = Date.now();
    let latestCart = null;

    try {
      latestCart = await this.fetchCart();
    } catch (error) {}

    while (Date.now() - startedAt < timeout) {
      if (latestCart && this.cartHasCurrentProtection(latestCart, selectedVariant)) return latestCart;
      await this.delay(interval);

      try {
        latestCart = await this.fetchCart();
      } catch (error) {}
    }

    return latestCart;
  }

  async waitForProtectionRemoval(timeout = 1800, interval = 140) {
    const startedAt = Date.now();
    let latestCart = null;

    try {
      latestCart = await this.fetchCart();
    } catch (error) {}

    while (Date.now() - startedAt < timeout) {
      if (latestCart && this.findProtectionItems(latestCart).length === 0) return latestCart;
      await this.delay(interval);

      try {
        latestCart = await this.fetchCart();
      } catch (error) {}
    }

    return latestCart;
  }

  async removeProtection(cart) {
    const protectionLines = (cart?.items || [])
      .map((item, index) => ({
        item,
        line: index + 1,
      }))
      .filter(({ item }) => this.isProtectionItem(item))
      .sort((a, b) => b.line - a.line);

    if (!protectionLines.length) {
      this.syncCheckedState(false);
      this.syncCheckoutGuards(cart);
      return cart;
    }

    this.syncing = true;
    try {
      let response = cart;

      for (const { line } of protectionLines) {
        response = await this.cartRequest('cart/change.js', {
          line,
          quantity: 0,
          sections: this.sectionIds.join(','),
          sections_url: window.location.pathname,
        });
      }

      this.syncCheckedState(false);
      this.syncCheckoutGuards(response);
      this.dispatchCartUpdate(response);
      return response;
    } finally {
      this.syncing = false;
    }
  }

  async prepareCheckout() {
    if (!this.productHandle) return true;

    this.setBusy(true);
    this.setCheckoutBusy(true);
    this.setError('');

    try {
      const cart = await this.fetchCart();
      this.updateDisplayedPrice(cart);

      if (!this.isProtectionDesired()) {
        if (this.findProtectionItems(cart).length > 0) {
          await this.removeProtection(cart);
          const verificationCart = await this.waitForProtectionRemoval();
          this.syncCheckoutGuards(verificationCart);
          if (this.findProtectionItems(verificationCart).length > 0) {
            console.warn('Shipping protection removal was requested before checkout, but cart readback is delayed.');
          }
        }

        this.syncCheckedState(false);
        return true;
      }

      if (!this.hasNonProtectionItems(cart)) {
        this.syncCheckedState(false);
        return true;
      }

      const selectedVariant = await this.selectProtectionVariant(cart);
      const updatedCart = await this.upsertProtection(cart);
      const verificationCart =
        updatedCart?.items && this.cartHasCurrentProtection(updatedCart, selectedVariant)
          ? updatedCart
          : await this.waitForProtectionCart(selectedVariant);
      const hasProtection = this.cartHasCurrentProtection(verificationCart, selectedVariant);
      const hasAnyProtection = this.findProtectionItems(verificationCart || {}).length > 0;

      if (verificationCart) {
        this.updateDisplayedPrice(verificationCart);
        this.syncCheckoutGuards(verificationCart);
      }
      this.syncCheckedState(true);

      if (!hasProtection && !hasAnyProtection) {
        console.error('Shipping protection was not synchronized before checkout.');
        this.scheduleEnsureProtection(500);
        this.setError('Protection is updating. Please try again.', true);
        return false;
      }

      if (!hasProtection && hasAnyProtection) {
        console.warn('Shipping protection is present before checkout, but cart readback has not confirmed the exact price variant yet.');
      }

      this.setError('');
      return true;
    } catch (error) {
      console.error('Shipping protection checkout preparation failed', error);
      const fallbackCart = await this.fetchCart().catch(() => null);

      if (fallbackCart?.items) {
        this.updateDisplayedPrice(fallbackCart);
        this.syncCheckoutGuards(fallbackCart);

        const hasAnyProtection = this.findProtectionItems(fallbackCart).length > 0;
        if (!this.isProtectionDesired() || !this.hasNonProtectionItems(fallbackCart) || hasAnyProtection) {
          this.setError('');
          return true;
        }
      }

      if (this.isProtectionDesired()) this.scheduleEnsureProtection(500);
      this.setError('Protection is updating. Please try again.', true);
      return false;
    } finally {
      this.setBusy(false);
      this.setCheckoutBusy(false);
    }
  }

  async cartRequest(path, body) {
    const response = await fetch(`${this.cartRoot}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      if (response.ok) throw new Error('Cart request did not return JSON');
    }

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart request failed');
    }

    return data;
  }

  dispatchCartUpdate(resource) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource,
          sourceId: 'yc-shipping-protection',
          data: {
            source: 'yc-shipping-protection',
            sections: resource.sections || {},
            itemCount: resource.item_count,
          },
        },
      })
    );

    window.BWShippingBar?.schedule?.();
    window.BW_ShippingBar?.schedule?.();
  }
}

if (!customElements.get('yc-shipping-protection')) {
  customElements.define('yc-shipping-protection', YCShippingProtection);
}
